import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { StarRating } from "@/components/StarRating";
import { User, Star, Calendar, ExternalLink } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [judges, setJudges] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchMyReviews();
    }
  }, [user]);

  const fetchMyReviews = async () => {
    try {
      const response = await axios.get(`${API}/users/me/reviews`);
      setReviews(response.data);
      
      // Fetch judge info for each review
      const judgeIds = [...new Set(response.data.map((r) => r.judge_id))];
      const judgePromises = judgeIds.map((id) =>
        axios.get(`${API}/judges/${id}`).then((res) => [id, res.data])
      );
      const judgeResults = await Promise.all(judgePromises);
      setJudges(Object.fromEntries(judgeResults));
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-off-white py-8" data-testid="dashboard-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white gold-accent-top shadow-lg rounded-sm p-8 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-navy rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-navy">
                {user.display_name}
              </h1>
              <p className="text-slate-600">{user.email}</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="bg-white shadow rounded-sm p-6 text-center">
            <Star className="w-8 h-8 text-gold mx-auto mb-2" />
            <p className="font-heading text-3xl font-bold text-navy">
              {reviews.length}
            </p>
            <p className="text-slate-600">Reviews Written</p>
          </div>
          <div className="bg-white shadow rounded-sm p-6 text-center">
            <Calendar className="w-8 h-8 text-gold mx-auto mb-2" />
            <p className="font-heading text-3xl font-bold text-navy">
              {Object.keys(judges).length}
            </p>
            <p className="text-slate-600">Judges Rated</p>
          </div>
        </div>

        {/* Reviews */}
        <div className="bg-white shadow rounded-sm p-6">
          <h2 className="font-heading text-xl font-semibold text-navy mb-6">
            My Reviews
          </h2>

          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 bg-slate-100 animate-pulse rounded" />
              ))}
            </div>
          ) : reviews.length > 0 ? (
            <div className="space-y-4">
              {reviews.map((review) => {
                const judge = judges[review.judge_id];
                return (
                  <div
                    key={review.id}
                    className="bg-slate-50 p-6 rounded-sm border-l-4 border-gold"
                    data-testid={`my-review-${review.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        {judge ? (
                          <Link
                            to={`/judges/${judge.id}`}
                            className="font-heading text-lg font-semibold text-navy hover:text-gold transition-colors flex items-center gap-2"
                          >
                            {judge.name}
                            <ExternalLink className="w-4 h-4" />
                          </Link>
                        ) : (
                          <span className="font-heading text-lg font-semibold text-navy">
                            Loading...
                          </span>
                        )}
                        <p className="text-sm text-slate-500">
                          {formatDistanceToNow(new Date(review.created_at), {
                            addSuffix: true,
                          })}
                        </p>
                      </div>
                      <StarRating rating={review.overall_rating} size="sm" />
                    </div>

                    {review.comment && (
                      <p className="text-slate-700 mb-3">{review.comment}</p>
                    )}

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className="text-slate-500">Fairness: </span>
                        <span className="font-semibold">{review.fairness}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Knowledge: </span>
                        <span className="font-semibold">{review.knowledge}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Communication: </span>
                        <span className="font-semibold">{review.communication}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Professionalism: </span>
                        <span className="font-semibold">{review.professionalism}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-slate-50 rounded-sm">
              <Star className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading text-lg text-navy mb-2">
                No reviews yet
              </h3>
              <p className="text-slate-600">
                Start by browsing judges and sharing your experiences!
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
